import { PadBox, Separator, VertBox } from 'component/basics';
import type { ButtonType } from 'component/button';
import { CTAButton } from 'component/button';
import { View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';

import { BaseModal } from './base-modal';

type ModalButtonProps = {
    label: TLabel;
    onPress: () => void;
    disabled?: boolean;
    icon?: React.ReactNode;
    testID?: string;
    type?: ButtonType;
    inProgress?: boolean;
};

function ButtonPair({ primaryButton, secondaryButton }: { primaryButton: ModalButtonProps; secondaryButton: ModalButtonProps }) {
    return (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap-reverse', gap: 16, alignSelf: 'stretch' }}>
            <View style={{ flexGrow: 1, flexBasis: 200 }}>
                <CTAButton
                    disabled={secondaryButton.disabled}
                    icon={secondaryButton.icon}
                    inProgress={secondaryButton.inProgress}
                    label={secondaryButton.label}
                    onPress={secondaryButton.onPress}
                    testID={secondaryButton.testID}
                    type={secondaryButton.type ?? 'secondary'}
                    wide
                />
            </View>
            <View style={{ flexGrow: 1, flexBasis: 200 }}>
                <CTAButton
                    disabled={primaryButton.disabled}
                    icon={primaryButton.icon}
                    inProgress={primaryButton.inProgress}
                    label={primaryButton.label}
                    onPress={primaryButton.onPress}
                    testID={primaryButton.testID}
                    type={primaryButton.type ?? 'primary'}
                    wide
                />
            </View>
        </View>
    );
}

type FunctionalModalProps = {
    primaryButton: ModalButtonProps;
    secondaryButton: ModalButtonProps;
    headingLabel: TLabel;
    subheadingLabel?: TLabel;
    subheadingFormatParams?: FormatParams;
    subheadingContent?: React.ReactNode;
    content?: React.ReactNode;
    stickyButtons?: boolean;
    onClose: () => void;
    headingFormatParams?: FormatParams;
};

export function FunctionalModal({
    headingLabel,
    subheadingLabel,
    subheadingFormatParams,
    subheadingContent,
    content,
    primaryButton,
    secondaryButton,
    stickyButtons = false,
    onClose,
    headingFormatParams,
}: FunctionalModalProps) {
    const buttons = <ButtonPair primaryButton={primaryButton} secondaryButton={secondaryButton} />;

    const stickyFooter = stickyButtons ? (
        <>
            <Separator />
            <PadBox horiz={20} vert={20}>
                {buttons}
            </PadBox>
        </>
    ) : undefined;

    return (
        <BaseModal footer={stickyFooter} onClose={onClose}>
            <PadBox horiz={20} vert={32}>
                <VertBox gap={32} wide>
                    <BaseModal.Heading
                        headingFormatParams={headingFormatParams}
                        label={headingLabel}
                        subheadingContent={subheadingContent}
                        subheadingFormatParams={subheadingFormatParams}
                        subheadingLabel={subheadingLabel}
                    />
                    {content}
                    {!stickyButtons && buttons}
                </VertBox>
            </PadBox>
        </BaseModal>
    );
}
